// DEBT-VITA™ — Dashboard principal
// Orchestre : sélection profil + bouton calculer + bannière + 6 KPI + 8 graphes.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, RefreshCw, FileWarning, Settings } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  calculateDebtVita,
  getLatestDebtResult,
  listProjectProfiles,
  getDebtParameters,
} from "@/lib/debt";
import { getProject } from "@/lib/profiles";

import DebtAlertBanner from "@/components/debt/DebtAlertBanner";
import DebtKPICards from "@/components/debt/DebtKPICards";
import DebtNarrativePanel from "@/components/debt/DebtNarrativePanel";
import DebtReportDownloadButton from "@/components/debt/DebtReportDownloadButton";
import {
  Graph1Thermometer,
  Graph2Donut,
  Graph3LifeCalendar,
  Graph4Projection,
  Graph5Generations,
  Graph6DebtVsFiscality,
  Graph7ServiceComposition,
  Graph8Treemap,
} from "@/components/debt/DebtCharts";

function LoadingSkeleton() {
  return (
    <div className="space-y-3" data-testid="debt-dashboard-loading">
      <div className="h-20 animate-pulse rounded-xl bg-muted" />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-64 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    </div>
  );
}

function NoParamsCTA({ projectId }) {
  return (
    <Card className="border-amber-300/50 bg-amber-50/50 dark:bg-amber-950/20" data-testid="debt-no-params-cta">
      <CardContent className="flex items-start gap-3 p-5">
        <FileWarning className="mt-0.5 h-5 w-5 text-amber-700 dark:text-amber-400" />
        <div>
          <p className="font-bold text-amber-900 dark:text-amber-200">
            Aucun paramètre de dette saisi
          </p>
          <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
            Configurez les paramètres macroéconomiques de la dette publique
            pour activer le dashboard DEBT-VITA™.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function NoProfileCTA({ projectId }) {
  return (
    <Card className="border-amber-300/50 bg-amber-50/50 dark:bg-amber-950/20" data-testid="debt-no-profile-cta">
      <CardContent className="flex items-start gap-3 p-5">
        <FileWarning className="mt-0.5 h-5 w-5 text-amber-700 dark:text-amber-400" />
        <div>
          <p className="font-bold text-amber-900 dark:text-amber-200">
            Aucun profil contributeur disponible
          </p>
          <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
            Créez d'abord un profil contributeur pour ce projet (étape 2 du
            wizard) — il fournit le salaire de référence pour convertir les
            montants en jours de vie.
          </p>
          <Link
            to={`/app/projects/${projectId}/wizard/profiles`}
            className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-amber-900 underline hover:no-underline dark:text-amber-200"
            data-testid="debt-link-create-profile"
          >
            <Settings className="h-3.5 w-3.5" />
            Aller au wizard "Profil contribuable"
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DebtVitaDashboard({ projectId }) {
  const [bootstrapping, setBootstrapping] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState(null);
  const [params, setParams] = useState(null);
  const [results, setResults] = useState(null);
  const [project, setProject] = useState(null);

  // Initial bootstrap: load project + profiles + params + latest result
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      setBootstrapping(true);
      try {
        const [proj, profs, p, latest] = await Promise.all([
          getProject(projectId).catch(() => null),
          listProjectProfiles(projectId),
          getDebtParameters(projectId),
          getLatestDebtResult(projectId),
        ]);
        if (cancelled) return;
        setProject(proj);
        setProfiles(profs || []);
        setParams(p);
        if (latest) {
          setResults(latest);
          if (latest.contributor_profile_id) setProfileId(latest.contributor_profile_id);
        } else if (profs && profs.length > 0) {
          setProfileId(profs[0].id);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("DebtVita dashboard bootstrap failed:", e);
        toast.error("Impossible de charger le dashboard DEBT-VITA™.");
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    }
    boot();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  async function runCalculate() {
    if (!profileId) {
      toast.error("Sélectionnez d'abord un profil contributeur.");
      return;
    }
    setCalculating(true);
    try {
      const r = await calculateDebtVita(projectId, profileId, true);
      if (r) {
        setResults(r);
        toast.success("Calcul DEBT-VITA™ effectué.");
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Calcul impossible.";
      toast.error(typeof msg === "string" ? msg : "Calcul impossible.");
    } finally {
      setCalculating(false);
    }
  }

  const headerControls = useMemo(
    () => (
      <div
        className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3"
        data-testid="debt-dashboard-controls"
      >
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
            Profil contributeur de référence
          </label>
          <Select
            value={profileId || ""}
            onValueChange={(v) => setProfileId(v)}
            disabled={profiles.length === 0}
          >
            <SelectTrigger className="mt-1 h-9" data-testid="debt-profile-select">
              <SelectValue placeholder="Aucun profil" />
            </SelectTrigger>
            <SelectContent>
              {profiles.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} — {p.monthly_salary} {params?.currency || "UM"}/mois
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={runCalculate}
          disabled={!params || !profileId || calculating}
          className="bg-red-700 text-white hover:bg-red-800"
          data-testid="debt-recalculate-btn"
        >
          {calculating ? (
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Calculator className="mr-2 h-4 w-4" />
          )}
          {results ? "Recalculer" : "Calculer"}
        </Button>
        {results?.calculated_at ? (
          <span className="text-[11px] text-muted-foreground" data-testid="debt-last-calc">
            Dernier calcul :{" "}
            {new Date(results.calculated_at).toLocaleString("fr-FR")}
          </span>
        ) : null}
        <div className="ml-auto">
          <DebtReportDownloadButton
            project={project || { id: projectId }}
            params={params}
            results={results}
            narrative={results?.narrative_analysis || null}
            disabled={!results}
          />
        </div>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileId, profiles, params, calculating, results, projectId, project]
  );

  if (bootstrapping) return <LoadingSkeleton />;

  if (!params) {
    return (
      <div className="space-y-4" data-testid="debt-dashboard">
        <NoParamsCTA projectId={projectId} />
      </div>
    );
  }

  if (profiles.length === 0) {
    return (
      <div className="space-y-4" data-testid="debt-dashboard">
        <NoProfileCTA projectId={projectId} />
      </div>
    );
  }

  if (!results) {
    return (
      <div className="space-y-4" data-testid="debt-dashboard">
        {headerControls}
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            Cliquez sur <strong>Calculer</strong> pour générer les 7 indicateurs
            vitalistes et les 8 graphiques.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="debt-dashboard">
      {headerControls}

      {/* ZONE 1 — Bannière d'alerte */}
      <DebtAlertBanner alertLevel={results.alert_level} />

      {/* ZONE 2 — 6 KPI cards */}
      <DebtKPICards results={results} debtParams={params} />

      {/* ZONE 3 — 8 graphiques en 2 colonnes responsives */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="debt-graphs-grid">
        <Graph1Thermometer results={results} />
        <Graph2Donut results={results} debtParams={params} />
        <Graph3LifeCalendar results={results} />
        <Graph4Projection results={results} debtParams={params} />
        <Graph5Generations results={results} debtParams={params} />
        <Graph6DebtVsFiscality results={results} />
        <Graph7ServiceComposition results={results} debtParams={params} />
        <Graph8Treemap results={results} debtParams={params} />
      </div>

      {/* ZONE 4 — Analyse narrative LLM (cached, 1 appel max) */}
      <DebtNarrativePanel
        projectId={projectId}
        hasResults={!!results}
        initialNarrative={
          results?.narrative_analysis
            ? {
                narrative: results.narrative_analysis,
                generated_at: results.narrative_generated_at,
              }
            : null
        }
      />
    </div>
  );
}
